import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { REPORT_LABELS, type ReportFilters, type ReportKind } from './types';
import type { ReportData } from './useReports';

const NAVY: [number, number, number] = [17, 53, 84];
const INCOME: [number, number, number] = [22, 163, 74];
const EXPENSE: [number, number, number] = [220, 38, 38];

const fmtBrl = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

interface Input {
  kind: ReportKind;
  filters: ReportFilters;
  data: ReportData;
  clientName: string;
  accountName?: string;
  counterpartyName?: string;
  costCenterName?: string;
  categoryName?: string;
}

export function exportReportPdf({
  kind,
  filters,
  data,
  clientName,
  accountName,
  counterpartyName,
  costCenterName,
  categoryName,
}: Input): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  const isIncome = kind === 'receivable' || kind === 'received';
  const accent = isIncome ? INCOME : EXPENSE;

  // Header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 90, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(REPORT_LABELS[kind], margin, 40);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(clientName, margin, 60);

  doc.setFontSize(9);
  doc.text(
    `Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
    pageWidth - margin,
    60,
    { align: 'right' },
  );

  // Period + filtros
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  let cursorY = 115;

  const period = `${format(filters.fromDate, 'dd/MM/yyyy', { locale: ptBR })} a ${format(
    filters.toDate,
    'dd/MM/yyyy',
    { locale: ptBR },
  )}`;
  doc.setFont('helvetica', 'bold');
  doc.text('Período: ', margin, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(period, margin + 50, cursorY);
  cursorY += 16;

  const appliedFilters: string[] = [];
  if (accountName) appliedFilters.push(`Conta: ${accountName}`);
  if (counterpartyName) appliedFilters.push(`Contraparte: ${counterpartyName}`);
  if (costCenterName) appliedFilters.push(`C. Custo: ${costCenterName}`);
  if (categoryName) appliedFilters.push(`Categoria: ${categoryName}`);
  if (appliedFilters.length > 0) {
    doc.text(`Filtros: ${appliedFilters.join(' · ')}`, margin, cursorY);
    cursorY += 16;
  }

  cursorY += 4;

  // Resumo
  doc.setFillColor(245, 247, 250);
  doc.rect(margin, cursorY, pageWidth - 2 * margin, 56, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text('Lançamentos', margin + 12, cursorY + 18);
  doc.text('Total', margin + 180, cursorY + 18);
  doc.text('Ticket médio', margin + 360, cursorY + 18);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(20, 20, 20);
  doc.text(String(data.count), margin + 12, cursorY + 42);

  doc.setTextColor(...accent);
  doc.text(fmtBrl(data.totalAmount), margin + 180, cursorY + 42);

  doc.setTextColor(20, 20, 20);
  doc.text(fmtBrl(data.averageAmount), margin + 360, cursorY + 42);

  cursorY += 76;

  // Tabela
  autoTable(doc, {
    startY: cursorY,
    margin: { left: margin, right: margin },
    head: [['Data', 'Descrição', 'Contraparte', 'Conta', 'Categoria', 'Valor']],
    body: data.rows.map((r) => {
      const dateForRow =
        kind === 'paid' || kind === 'received' ? (r.paymentDate ?? r.dueDate) : r.dueDate;
      return [
        format(dateForRow, 'dd/MM/yyyy', { locale: ptBR }),
        r.description,
        r.counterpartyName ?? '—',
        r.accountName,
        r.categorySplits && r.categorySplits.length > 0
          ? `Múltiplas (${r.categorySplits.length})`
          : r.categoryName,
        fmtBrl(Number(r.amount) + Number(r.interest ?? 0) + Number(r.fine ?? 0)),
      ];
    }),
    headStyles: {
      fillColor: NAVY,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 70 },
      5: { halign: 'right', textColor: accent, fontStyle: 'bold' },
    },
    didDrawPage: () => {
      const pageCount = doc.getNumberOfPages();
      const currentPage = doc.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Página ${currentPage} de ${pageCount}`,
        pageWidth - margin,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' },
      );
    },
  });

  const filename = `${kind}-${format(filters.fromDate, 'yyyy-MM-dd')}-a-${format(
    filters.toDate,
    'yyyy-MM-dd',
  )}.pdf`;
  doc.save(filename);
}
